import { Controller } from "@hotwired/stimulus";

// Composer for the unified inbox at /admin/messages.
// Handles:
//   - Auto-resize textarea as content grows (capped at maxRowsValue)
//   - Live character + SMS-segment count below the textarea
//   - Cmd/Ctrl+Enter submits the form. Plain Enter inserts a newline.
//   - "Draft with Oscar" button → POSTs to draftUrlValue, replaces the textarea
//     with the returned draft, refocuses for editing.
//   - Submit button is disabled when the textarea is empty so staff can't fire
//     blank sends.
//
// V1 segmenting is length-only: 160 chars per segment (GSM 7-bit). Using
// Unicode characters in practice drops the limit to 70/segment, but the cost
// signal here is "1 vs 2+ segments" not exactness — keep it simple.
export default class extends Controller {
  static targets = ["textarea", "charCount", "submit", "draftButton"];
  static values = {
    maxRows: { type: Number, default: 6 },
    draftUrl: String,
    csrf: String,
  };

  connect() {
    if (!this.hasTextareaTarget) return;
    this.lineHeight = this.computeLineHeight();
    this.update();
    this.textareaTarget.focus();
  }

  // Triggered by data-action="input->composer#update keydown->composer#keydown"
  update() {
    this.autoResize();
    this.updateCharCount();
    this.updateSubmitState();
  }

  keydown(event) {
    // Cmd+Enter (Mac) or Ctrl+Enter (Win/Linux) submits the form.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submit();
    }
  }

  submit() {
    if (this.textareaTarget.value.trim().length === 0) return;
    this.element.requestSubmit();
  }

  // Called by the Draft button. data-action="composer#draft"
  async draft(event) {
    event?.preventDefault();
    if (!this.hasDraftUrlValue || this.draftUrlValue === "") return;

    const button = this.hasDraftButtonTarget ? this.draftButtonTarget : event?.currentTarget;
    if (button) {
      button.disabled = true;
      button.dataset.originalText = button.innerHTML;
      button.innerHTML = "Drafting…";
    }

    try {
      const response = await fetch(this.draftUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfValue,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        this.flashError(data.error || "Couldn't draft a reply right now.");
        return;
      }

      const data = await response.json();
      this.textareaTarget.value = data.content || "";
      this.update();
      this.textareaTarget.focus();
      // Place caret at end so staff can immediately edit/append.
      const len = this.textareaTarget.value.length;
      this.textareaTarget.setSelectionRange(len, len);
    } catch (err) {
      this.flashError("Couldn't reach Oscar right now.");
    } finally {
      if (button) {
        button.disabled = false;
        if (button.dataset.originalText) {
          button.innerHTML = button.dataset.originalText;
          delete button.dataset.originalText;
        }
      }
    }
  }

  // Called by the Turbo Stream send response — clears the composer post-send.
  reset() {
    if (!this.hasTextareaTarget) return;
    this.textareaTarget.value = "";
    this.update();
    this.textareaTarget.focus();
  }

  // ── Internals ─────────────────────────────────────────────

  autoResize() {
    const ta = this.textareaTarget;
    ta.style.height = "auto";
    const maxHeight = this.lineHeight * this.maxRowsValue;
    const newHeight = Math.min(ta.scrollHeight, maxHeight);
    ta.style.height = `${newHeight}px`;
    ta.style.overflowY = ta.scrollHeight > maxHeight ? "auto" : "hidden";
  }

  updateCharCount() {
    if (!this.hasCharCountTarget) return;
    const len = this.textareaTarget.value.length;
    if (len === 0) {
      this.charCountTarget.textContent = "";
      this.charCountTarget.classList.remove("text-amber-600", "dark:text-amber-400");
      return;
    }
    const segments = Math.max(1, Math.ceil(len / 160));
    this.charCountTarget.textContent = `${len} ${len === 1 ? "char" : "chars"} · ${segments} ${segments === 1 ? "segment" : "segments"}`;
    // Warn at 2+ segments — staff sees the cost going up.
    this.charCountTarget.classList.toggle("text-amber-600", segments >= 2);
    this.charCountTarget.classList.toggle("dark:text-amber-400", segments >= 2);
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget) return;
    const empty = this.textareaTarget.value.trim().length === 0;
    this.submitTarget.disabled = empty;
    this.submitTarget.classList.toggle("opacity-50", empty);
    this.submitTarget.classList.toggle("cursor-not-allowed", empty);
  }

  computeLineHeight() {
    const computed = window.getComputedStyle(this.textareaTarget);
    const lh = parseFloat(computed.lineHeight);
    if (!Number.isNaN(lh) && lh > 0) return lh;
    // Fallback: ~1.5 × font-size.
    const fontSize = parseFloat(computed.fontSize) || 14;
    return fontSize * 1.5;
  }

  flashError(msg) {
    if (!this.hasCharCountTarget) {
      console.warn("[composer] " + msg); // eslint-disable-line no-console
      return;
    }
    this.charCountTarget.textContent = msg;
    this.charCountTarget.classList.add("text-red-600", "dark:text-red-400");
    setTimeout(() => {
      this.charCountTarget.classList.remove("text-red-600", "dark:text-red-400");
      this.updateCharCount();
    }, 3000);
  }
}
